import React, { useEffect, useState, useRef } from "react";
import { DateRangePicker } from "react-date-range";
import { formatDMY, PrimaryColor } from "../common";
import { formatDateString } from "../pages/Dashboard/Auction/Auction";
import { enGB } from "date-fns/locale";
import ReactDOM from "react-dom";

interface DynamicDatePickerProps {
  setDate: (dates: { startDate: Date; endDate: Date; key: string }[]) => void;
  defaultDates?: { startDate: Date; endDate: Date; key: string };
  minDate?: string;
  maxDate?: string;
  selectedDates?:
    | { startDate: Date; endDate: Date; key: string }
    | Array<{ value: string; startDate?: Date; endDate?: Date }>;
  buttonClassName?: string;
}

// Format date with ordinal suffix (e.g., "23rd Feb 2025")
const formatDateWithOrdinal = (date: Date | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return "";
  }

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Determine ordinal suffix
  let suffix = "th";
  if (day === 1 || day === 21 || day === 31) {
    suffix = "st";
  } else if (day === 2 || day === 22) {
    suffix = "nd";
  } else if (day === 3 || day === 23) {
    suffix = "rd";
  }

  return `${day}${suffix} ${month} ${year}`;
};

export const DynamicDatePicker: React.FC<DynamicDatePickerProps> = ({
  setDate,
  defaultDates,
  selectedDates = [],
  minDate = "2021-01-01",
  maxDate = "2026-12-31",
  buttonClassName,
}) => {
  const [visible, setVisible] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  const [tempDateRange, setTempDateRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(null);

  // Button reference to position the picker relative to button
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (defaultDates) {
      setDateRange(defaultDates);
    }
  }, [defaultDates]);

  const handleDateChange = (item: any) => {
    setTempDateRange({
      startDate: item.selection.startDate || new Date(),
      endDate: item.selection.endDate || new Date(),
      key: item.selection.key || "selection",
    });
  };

  const handleSubmit = () => {
    // Only proceed if tempDateRange exists
    if (tempDateRange) {
      // Format the dates in a consistent format for comparison
      const formattedStartDate =
        tempDateRange.startDate.toLocaleDateString("en-GB");
      const formattedEndDate =
        tempDateRange.endDate.toLocaleDateString("en-GB");

      // Check if the date range matches the defaultDates (if provided)
      const matchesDefault = defaultDates
        ? defaultDates.startDate.toLocaleDateString("en-GB") ===
            formattedStartDate &&
          defaultDates.endDate.toLocaleDateString("en-GB") === formattedEndDate
        : false;

      // Update local confirmed state
      setDateRange(tempDateRange);

      // Only set the external date if it's a valid range (different start and end dates)
      // and it doesn't match the already set default dates
      if (!matchesDefault) {
        // Convert dates to the required format and set them
        setDate([
          {
            startDate: new Date(
              formattedStartDate.split("/").reverse().join("-"),
            ),
            endDate: new Date(formattedEndDate.split("/").reverse().join("-")),
            key: "selection",
          },
        ]);
      }
    }

    // Close the date picker regardless of whether we set a new date
    setVisible(false);
  };

  // Compute all disabled dates
  const disabledDates: Date[] = [];

  if (typeof selectedDates === "object" && !Array.isArray(selectedDates)) {
    let current = new Date(selectedDates.startDate);
    const end = new Date(selectedDates.endDate);
    while (current <= end) {
      disabledDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  } else if (Array.isArray(selectedDates)) {
    selectedDates.forEach((range) => {
      const date = String(range.value).slice(0, 21).split(",");
      let current = new Date(date[0]);
      const end = new Date(date[1]);
      while (current <= end) {
        disabledDates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    });
  }

  // Close picker when clicking outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Skip if click was on the button itself
      if (buttonRef.current?.contains(e.target as Node)) return;

      const datePickerElements = document.querySelectorAll(
        ".date-selection-wrapper, .rdrDateRangePickerWrapper",
      );
      let clickedInside = false;

      datePickerElements.forEach((el) => {
        if (el.contains(e.target as Node)) {
          clickedInside = true;
        }
      });

      if (!clickedInside) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible]);

  // Render the DatePicker at the top of the page using portal
  const renderDatePickerPortal = () => {
    if (!visible) return null;

    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
        <div
          className="date-selection-wrapper  shadow-2xl rounded-md pointer-events-auto"
          style={{
            maxWidth: "96vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <div className="">
            <DateRangePicker
              onChange={handleDateChange}
              showPreview={true}
              dragSelectionEnabled={false}
              minDate={new Date(minDate)}
              maxDate={new Date(maxDate)}
              moveRangeOnFirstSelection={false}
              rangeColors={[PrimaryColor, PrimaryColor, PrimaryColor]}
              color={PrimaryColor}
              locale={enGB}
              months={2}
              ranges={
                tempDateRange
                  ? [tempDateRange]
                  : [
                      {
                        startDate: dateRange.startDate || new Date(),
                        endDate: dateRange.endDate || new Date(),
                        key: "selection",
                      },
                    ]
              }
              direction="horizontal"
              disabledDates={disabledDates}
              inputRanges={[]}
            />
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={handleSubmit}
                className="px-6 py-2 bg-[#F1935C] text-white rounded-lg"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  };

  // When custom buttonClassName is provided, don't use date-range class to avoid conflicting styles
  const spanClassName = buttonClassName ? "" : "date-range";

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!visible) {
            setTempDateRange(dateRange);
          }
          setVisible(!visible);
        }}
        className={buttonClassName || "date-range-button"}
      >
        {defaultDates ? (
          <>
            <span className={spanClassName}>
              {formatDateWithOrdinal(dateRange!.startDate)} -{" "}
              {formatDateWithOrdinal(dateRange!.endDate)}
            </span>
          </>
        ) : (
          <span className={spanClassName}>Add Date Range</span>
        )}
      </button>

      {renderDatePickerPortal()}
    </div>
  );
};
